import { useEffect, useState } from 'react';
import { api, type Asset, type TalentRow } from '../../lib/api';
import { sayQuote } from '../../lib/quick';
import { sayWhat } from '../../lib/models';
import { GAVE_UP, GIVE_UP_AFTER, LOOK_EVERY_MS, saySeconds, useElapsed } from '../../lib/waiting';
import { ModelPicker } from '../ModelPicker';
import { ModelOptions } from '../ModelOptions';
import { OpenInPro, QuoteLine, SendButton, Stage } from './parts';
import { useBench } from './useBench';
import type { TabProps } from './Quick';

// The Video tab: one shot from a sentence, on the model you picked.
//
// The Scene Builder plans ten shots from a premise and buys them in one
// press; this buys one. Same route, same cast rule — a shot is held
// together by a portrait, so anybody without one is refused by name. The
// model comes from the catalogue (204), shown as Venice shows it: the
// name, the privacy word, the price a second, the lengths and sizes it
// makes. The price on the button is the server's quote for one shot as it
// stands, asked again whenever the choice changes; the option's own
// credits stand in while the quote is in flight, and no rate is typed here.

const SHOT_SECONDS = 8;
const MAX_PEOPLE = 3;
const MIN_PROMPT = 10;

export function QuickVideo({ brand, models, pass, onUsedPass, onOpenInPro, onGo, prefill, onUsedPrefill, onError }: TabProps) {
  const [prompt, setPrompt] = useState('');
  const [cast, setCast] = useState<string[]>([]);
  const [talent, setTalent] = useState<TalentRow[]>([]);
  const [planId, setPlanId] = useState<string | null>(null);
  const [shot, setShot] = useState<{ status: string; error: string | null; video: string | null; poster: string | null } | null>(null);
  const [stalled, setStalled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [seeded, setSeeded] = useState<string | null>(null);
  const waited = useElapsed(startedAt);
  const brandId = brand?.id ?? null;
  const { model, setModel, sel, setSel, quote, credits } = useBench('video', models.video, (s) => ({ shots: 1, seconds: s.seconds ?? SHOT_SECONDS }), 1);

  useEffect(() => {
    let live = true;
    api.talent().then((r) => { if (live) setTalent(r.talent); }).catch(() => undefined);
    return () => { live = false; };
  }, []);

  // A proposal fills the form and is let go. Everyone with a ready portrait is cast, as on the Scene Builder.
  useEffect(() => {
    if (!prefill || prefill.action !== 'plan_scenes') return;
    const premise = String(prefill.args.premise ?? '');
    if (premise) setPrompt(premise);
    setCast(talent.filter((t) => t.portrait === 'ready').slice(0, MAX_PEOPLE).map((t) => t.id));
    onUsedPrefill?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, talent.length]);

  // A picture handed from the Library seeds the sentence: its description if
  // it has been read, its name if not. The shot is generated from the words,
  // not cut from the picture, and the note under the stage says so.
  useEffect(() => {
    if (!pass || pass.tab !== 'video') return;
    onUsedPass();
    api.assets(brandId).then((all) => {
      const a: Asset | undefined = all.find((x) => x.id === pass.assetId);
      if (!a) return;
      const words = a.description?.trim() || a.name?.trim() || '';
      if (words) { setPrompt(words); setSeeded(a.name?.trim() || a.storage_key.split('/').pop() || 'that picture'); }
    }).catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass]);

  const chosen = cast.map((id) => talent.find((t) => t.id === id)).filter((t): t is TalentRow => Boolean(t));
  const faceless = chosen.filter((t) => t.portrait !== 'ready');
  const why = !model ? 'Choose a model'
    : prompt.trim().length < MIN_PROMPT ? `Describe the shot — ${MIN_PROMPT} characters at least`
    : faceless.length ? `${faceless.map((t) => t.name).join(', ')} ${faceless.length === 1 ? 'has' : 'have'} no portrait — a shot has nothing to hold their face to`
    : quote && !quote.balance.ok ? `Short by ${quote.balance.shortBy.toLocaleString()} credits`
    : null;

  // One shot is minutes. Watched on the shared cadence with a visible give-up.
  useEffect(() => {
    if (!planId) return;
    let live = true; let looks = 0;
    setStalled(false);
    const look = async () => {
      try {
        const r = await api.scenePlan(planId);
        const s = r.scenes[0];
        if (!live || !s) return;
        setShot({ status: s.status, error: s.error, video: s.video, poster: s.poster });
        if (s.status === 'ready' || s.status === 'error') { clearInterval(t); setStartedAt(null); return; }
        if (looks++ >= GIVE_UP_AFTER) { clearInterval(t); setStalled(true); }
      } catch { /* the next look tries again */ }
    };
    const t = setInterval(() => { void look(); }, LOOK_EVERY_MS);
    void look();
    return () => { live = false; clearInterval(t); };
  }, [planId, stalled]);

  async function send() {
    if (!model) return;
    setBusy(true); setStartedAt(Date.now()); setShot(null); setPlanId(null); onError(null);
    try {
      const r = await api.buildPlan({
        name: prompt.trim().slice(0, 60), premise: prompt.trim(), brandId,
        model: model.id, ...(sel.resolution ? { resolution: sel.resolution } : {}), ...(sel.audio !== undefined ? { audio: sel.audio } : {}),
        scenes: [{ beat: 'The shot', action: prompt.trim(), cast, speaker: null, line: null }],
      });
      setPlanId(r.planId);
    } catch (e) { onError((e as Error).message); setStartedAt(null); } finally { setBusy(false); }
  }

  return (
    <div className="quick-tab">
      <Stage empty="Your shot lands here — a few minutes, longer from cold.">
        {shot?.status === 'ready' && shot.video ? <video src={shot.video} poster={shot.poster ?? undefined} controls autoPlay />
          : shot?.status === 'error' ? <p className="quick-stage-empty" role="alert">{shot.error ?? 'The shot failed.'}</p>
          : planId ? <p className="quick-stage-empty">Generating… {saySeconds(waited)}</p>
          : null}
      </Stage>
      {stalled && <p className="sub-dim quick-note">{GAVE_UP} <button type="button" className="btn tiny" onClick={() => setStalled(false)}>Look again</button></p>}
      {seeded && <p className="sub-dim quick-note">Starting from {seeded}: its words are the sentence. The shot is generated from them, not cut from the picture.</p>}

      <ModelPicker bench="video" variant="card" models={models.video ?? null} value={model} onChange={setModel} />
      <ModelOptions bench="video" model={model} value={sel} onChange={setSel} />

      <textarea className="quick-prompt" value={prompt} rows={3} maxLength={2000} placeholder="Ralph and Shirley at a wooden table, steam off the bowls, warm lamplight, Ralph reaches for the noodles" onChange={(e) => setPrompt(e.target.value)} />

      <div className="quick-chips" data-tool="quick-cast">
        <span className="sub-dim">Who is in it</span>
        {talent.length === 0 && <span className="sub-dim">Nobody yet — a shot is held together by a portrait. Add talent in Pro.</span>}
        {talent.map((t) => (
          <button key={t.id} type="button" className={`chip${cast.includes(t.id) ? ' on' : ''}`}
                  disabled={!cast.includes(t.id) && cast.length >= MAX_PEOPLE}
                  title={t.portrait === 'ready' ? 'Their portrait holds their face through the shot' : 'No portrait yet — draw one on Talent first'}
                  onClick={() => setCast((c) => (c.includes(t.id) ? c.filter((x) => x !== t.id) : [...c, t.id]))}>
            {t.name}{t.portrait !== 'ready' ? <span className="sub-dim"> · no portrait</span> : null}
          </button>
        ))}
      </div>

      <div className="quick-actions">
        <SendButton label="Make the shot" busyLabel="Commissioning…" busy={busy} credits={credits} why={why} onClick={() => void send()} />
        <OpenInPro what="The Video Lab's Scene Builder: a premise into ten shots, planned before they are bought." onClick={() => onOpenInPro({
          bench: 'video', move: 'scene-quote',
          prefill: { action: 'plan_scenes', say: '', args: { premise: prompt.trim(), count: 1 }, bench: 'video', move: 'scene-quote', inline: false },
        })} />
        <button type="button" className="btn tiny" onClick={() => onGo('start', 'idea-story')}>Start from an idea</button>
      </div>
      <QuoteLine text={credits === null ? null
        : `${sayQuote(credits, sayWhat('video', model, { ...sel, seconds: sel.seconds ?? SHOT_SECONDS }), quote?.balance)}${quote ? '' : ' Asking for the exact quote…'}${quote?.vendorDrift ? ' The price list is being checked against today\'s vendor rate.' : ''}`} />
    </div>
  );
}
